import React, { createContext, useContext, useMemo, useState } from "react";
import { FieldValues, FormProvider } from "react-hook-form";
import { Reception, Suborder } from "$types";
import { useReferencingFormLogic } from "../hooks/useReferencingFormLogic";
import useCollection from "$hooks/useCollection";
import { ExportOrderReferenceType } from "$types/ExportOrderReferenceType";
import { OrderReference as OrderReferenceType } from "$types/OrderReference";
import { ProductReferenceList } from "$types/ProductReferenceList";
import useReferencingCategory from "$hooks/useReferencingCategory";
import useIsProductDeletable from "$hooks/useIsProductDeletable";
import useReferencing from "$hooks/useReferencing";
import { Category } from "$types/CategoryType";
import ReceptionProduct from "$types/ReceptionProduct";

interface EditedBlock {
  id: string;
  type: string;
}

interface EditedBlockCollection {
  items: EditedBlock[];
  add: (item: EditedBlock) => void;
}

export interface FormValues {
  orderReferences: OrderReferenceType[];
  productReferenceLists: ProductReferenceList[];
  receptionProduct: ReceptionProduct | null;
}

type ReferencingFormContextType = {
  focusedReceptionReferenceId?: number;
  isReadOnly: boolean;
  warehouse?: string;
  isSavingReception: boolean;
  isSavingProducts: boolean;
  reception: Reception;
  setIndexToDelete: (index: number | null) => void;
  onSubmit: (data: FormValues) => Promise<any>;
  togglePopin: (open: boolean) => void;
  editedBlocks: EditedBlock[];
  addToEditedBlocks: (block: EditedBlock) => void;
  removeFromEditedBlocks: (block: EditedBlock) => void;
  resetEditedBlocks: () => void;
  pendingDeletions: string[];
  setPendingDeletions: (deletions: string[]) => void;
  toDeleteReferences: string[];
  setToDeleteReferences: (refs: string[]) => void;
  toKeepReferences: string[];
  setToKeepReferences: (refs: string[]) => void;
  toKeepIdenticalReferences: string[];
  setToKeepIdenticalReferences: (refs: string[]) => void;
  toDeleteOrderRef: OrderReferenceType | null;
  setToDeleteOrderRef: (ref: OrderReferenceType | null) => void;
  toDeleteProductRefList: ProductReferenceList | null;
  setToDeleteProductRefList: (ref: ProductReferenceList | null) => void;
  openDeleteModal: boolean;
  setOpenDeleteModal: (open: boolean) => void;
  openCancelModal: boolean;
  setOpenCancelModal: (open: boolean) => void;
  confirmedDeletions: boolean;
  setConfirmedDeletions: (confirmed: boolean) => void;
  handleOrderReference: (
    params: ExportOrderReferenceType,
    isReception: boolean
  ) => Promise<void>;
  handleCancelUpdate: () => void;
  refreshRefItem: (orderRefItem: OrderReferenceType) => OrderReferenceType;
  onDeleteProducts: () => Promise<void>;
  hasReferencingCategory: boolean;
  loading: boolean;
  referencingCategory: Category;
  isDeletable: (
    reference: string,
    companyId: string,
    receptionId: string | number
  ) => Promise<boolean>;
  orderRefFields: OrderReferenceType[];
  productRefListFields: ProductReferenceList[];
  removeOrderRef: (index: number) => void;
  prependOrderRef: (orderRef: OrderReferenceType) => void;
  prependProductRefList: (productRefList: ProductReferenceList) => void;
  removeProductRefList: (index: number) => void;
  getDefaultOrderReference: (
    category: Category,
    suborders: Suborder[]
  ) => OrderReferenceType;
  getDefaultProductReference: (suborders: Suborder[], data: any) => any;
  fromProductToOrderReferencing: (
    category: Category,
    prependOrderRef: (orderRef: OrderReferenceType) => void,
    index: number,
    productRefList: ProductReferenceList,
    removeProductRefList: (index: number) => void
  ) => void;
  fromOrderToProductReferencing: (
    index: number,
    orderRef: OrderReferenceType,
    prependProductRefList: (productRefList: ProductReferenceList) => void,
    orderRefItem: OrderReferenceType,
    removeOrderRef: (index: number) => void
  ) => void;
  onSelectSuborders: (newSuborders: Suborder[]) => void;
  selectableOrders: any[]; // TODO: Type this based on your Order type
  initialReceptionReferences: any[]; // TODO: Type this based on your ReceptionReference type
  selectableSuborders: Suborder[];
  setSelectableSuborders: (suborders: Suborder[]) => void;
  scrollToRef: React.RefObject<HTMLDivElement>;
  handleOnSubmit: (data: FieldValues, closePopin: boolean) => Promise<void>;
  deletePreviousOrderRef: (orderRef: OrderReferenceType | null) => void;
  deletePreviousProductListRef: (
    productRefList: ProductReferenceList | null
  ) => void;
  getPreviousProductListFromOrderRef: (
    orderRef: OrderReferenceType
  ) => ProductReferenceList;
  getPreviousOrderRefFromProductList: (
    productRefList: ProductReferenceList
  ) => OrderReferenceType;
  isLoading: boolean;
};

interface Props {
  children: React.ReactNode;
  focusedReceptionReferenceId?: number;
  isReadOnly: boolean;
  warehouse?: string;
  isSavingReception: boolean;
  reception: Reception;
  onSubmit: (data: FormValues) => Promise<any>;
  togglePopin: (open: boolean) => void;
}

export const ReferencingFormContext = createContext<
  ReferencingFormContextType | undefined
>(undefined);

export const useReferencingForm = (): ReferencingFormContextType => {
  const context = useContext(ReferencingFormContext);
  if (!context) {
    throw new Error(
      "useReferencingForm must be used within ReferencingFormProvider"
    );
  }
  return context;
};

export const ReferencingFormProvider: React.FC<Props> = ({
  children,
  focusedReceptionReferenceId,
  isReadOnly,
  warehouse,
  isSavingReception,
  reception,
  onSubmit,
  togglePopin,
}) => {
  const [isSavingProducts, setIsSavingProducts] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [confirmedDeletions, setConfirmedDeletions] = useState(false);
  const [toDeleteReferences, setToDeleteReferences] = useState<string[]>([]);
  const [toKeepReferences, setToKeepReferences] = useState<string[]>([]);
  const [toKeepIdenticalReferences, setToKeepIdenticalReferences] = useState<
    string[]
  >([]);
  const [pendingDeletions, setPendingDeletions] = useState<string[]>([]);
  const [toDeleteOrderRef, setToDeleteOrderRef] =
    useState<OrderReferenceType | null>(null);
  const [toDeleteProductRefList, setToDeleteProductRefList] =
    useState<ProductReferenceList | null>(null);
  const [selectableSuborders, setSelectableSuborders] = useState<Suborder[]>(
    []
  );
  const scrollToRef = React.useRef<HTMLDivElement>(null);
  const [indexToDelete, setIndexToDelete] = useState<number | null>(null);

  const { isDeletable } = useIsProductDeletable();
  const { hasReferencingCategory, loading, referencingCategory } =
    useReferencingCategory(reception.company);

  const referencingHooks = useReferencing(reception.company);

  const {
    deletePreviousOrderRef,
    deletePreviousProductListRef,
    getPreviousProductListFromOrderRef,
    getPreviousOrderRefFromProductList,
  } = useReferencing(reception.company);

  const {
    items: editedBlockItems,
    addIfNotExistsOrReplace: addEditedBlock,
    remove: removeEditedBlock,
    reset: resetEditedBlocks,
  } = useCollection<EditedBlock>();

  // Create editedBlocks object that matches the expected structure
  const editedBlocksCollection: EditedBlockCollection = {
    items: editedBlockItems,
    add: addEditedBlock,
  };

  const formLogic = useReferencingFormLogic({
    reception,
    onSubmit,
    togglePopin,
    editedBlocks: editedBlocksCollection,
    resetEditedBlocks,
    setPendingDeletions,
    pendingDeletions,
    setIsSavingProducts,
    hasReferencingCategory,
    referencingCategory,
    addToEditedBlocks: addEditedBlock,
    ...referencingHooks,
    isDeletable,
    selectableSuborders,
    setSelectableSuborders,
    setToDeleteReferences,
    indexToDelete,
    setIndexToDelete,
    setOpenCancelModal,
    openCancelModal,
    deletePreviousOrderRef,
    deletePreviousProductListRef,
    getPreviousProductListFromOrderRef,
    setToKeepReferences,
    // setToKeepIdenticalReferences,
    setToDeleteOrderRef,
    setToDeleteProductRefList,
    setConfirmedDeletions,
    toDeleteOrderRef,
    toDeleteProductRefList,
    toKeepIdenticalReferences,
    setOpenDeleteModal,
    toDeleteReferences,
    getPreviousOrderRefFromProductList,
  });

  const value = useMemo(
    () => ({
      focusedReceptionReferenceId,
      isReadOnly,
      warehouse,
      refreshRefItem: formLogic.refreshRefItem,
      isSavingReception,
      isSavingProducts,
      reception,
      onSubmit,
      togglePopin,
      editedBlocks: editedBlockItems,
      addToEditedBlocks: addEditedBlock,
      removeFromEditedBlocks: removeEditedBlock,
      resetEditedBlocks,
      pendingDeletions,
      setPendingDeletions,
      toDeleteReferences,
      setToDeleteReferences,
      toKeepReferences,
      setToKeepReferences,
      toKeepIdenticalReferences,
      setToKeepIdenticalReferences,
      toDeleteOrderRef,
      setToDeleteOrderRef,
      toDeleteProductRefList,
      setToDeleteProductRefList,
      openDeleteModal,
      setOpenDeleteModal,
      openCancelModal,
      setOpenCancelModal,
      confirmedDeletions,
      setConfirmedDeletions,
      handleOrderReference: formLogic.handleOrderReference,
      handleCancelUpdate: formLogic.handleCancelUpdate,
      onDeleteProducts: formLogic.onDeleteProducts,
      hasReferencingCategory,
      loading,
      referencingCategory,
      isDeletable: isDeletable as ReferencingFormContextType["isDeletable"],
      orderRefFields: formLogic.orderRefFields,
      productRefListFields: formLogic.productRefListFields,
      removeOrderRef: formLogic.removeOrderRef,
      prependOrderRef: formLogic.prependOrderRef,
      prependProductRefList: formLogic.prependProductRefList,
      removeProductRefList: formLogic.removeProductRefList,
      getDefaultOrderReference: referencingHooks.getDefaultOrderReference,
      getDefaultProductReference: referencingHooks.getDefaultProductReference,
      fromProductToOrderReferencing:
        referencingHooks.fromProductToOrderReferencing,
      fromOrderToProductReferencing:
        referencingHooks.fromOrderToProductReferencing,
      onSelectSuborders: formLogic.onSelectSuborders,
      selectableOrders: formLogic.selectableOrders,
      initialReceptionReferences: formLogic.initialReceptionReferences,
      selectableSuborders,
      setSelectableSuborders,
      scrollToRef,
      handleOnSubmit: formLogic.handleOnSubmit,
      indexToDelete,
      setIndexToDelete,
      deletePreviousOrderRef,
      deletePreviousProductListRef,
      getPreviousProductListFromOrderRef,
      getPreviousOrderRefFromProductList,
      isLoading: formLogic.isLoading,
    }),
    [
      focusedReceptionReferenceId,
      isReadOnly,
      warehouse,
      formLogic.refreshRefItem,
      formLogic.handleOrderReference,
      formLogic.handleCancelUpdate,
      formLogic.onDeleteProducts,
      formLogic.orderRefFields,
      formLogic.productRefListFields,
      formLogic.removeOrderRef,
      formLogic.prependOrderRef,
      formLogic.prependProductRefList,
      formLogic.removeProductRefList,
      formLogic.onSelectSuborders,
      formLogic.selectableOrders,
      formLogic.initialReceptionReferences,
      formLogic.handleOnSubmit,
      formLogic.isLoading,
      isSavingReception,
      isSavingProducts,
      reception,
      onSubmit,
      togglePopin,
      editedBlockItems,
      addEditedBlock,
      removeEditedBlock,
      resetEditedBlocks,
      pendingDeletions,
      toDeleteReferences,
      toKeepReferences,
      toKeepIdenticalReferences,
      toDeleteOrderRef,
      toDeleteProductRefList,
      openDeleteModal,
      openCancelModal,
      confirmedDeletions,
      hasReferencingCategory,
      loading,
      referencingCategory,
      isDeletable,
      referencingHooks,
      selectableSuborders,
      indexToDelete,
      deletePreviousOrderRef,
      deletePreviousProductListRef,
      getPreviousProductListFromOrderRef,
      getPreviousOrderRefFromProductList,
    ]
  );

  return (
    <ReferencingFormContext.Provider value={value}>
      <FormProvider {...formLogic.form}>{children}</FormProvider>
    </ReferencingFormContext.Provider>
  );
};
